import { useState } from 'react';
import { useTodayReward, useClaimReward } from '../hooks/useRewards';
import { Gift, Flame, Check, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function DailyReward() {
  const { data: reward, isLoading } = useTodayReward();
  const claim = useClaimReward();
  const [showModal, setShowModal] = useState(false);

  const handleClaim = () => {
    claim.mutate(undefined, {
      onSuccess: (data: any) => {
        toast.success(`+${data.data.points} points claimed! 🔥 Streak: ${data.data.streak} days`);
        setShowModal(false);
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error || 'Failed to claim');
      },
    });
  };

  if (isLoading) return null;

  const isClaimed = reward?.claimed;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
          isClaimed
            ? 'bg-green-100 text-green-700'
            : 'bg-gradient-to-r from-orange-400 to-red-500 text-white hover:from-orange-500 hover:to-red-600 shadow-lg animate-pulse'
        }`}
      >
        {isClaimed ? <Check className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
        {isClaimed ? 'Claimed' : 'Daily Reward'}
        {!isClaimed && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
        )}
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift className="w-10 h-10 text-white" />
            </div>

            <h2 className="text-2xl font-bold mb-2">Daily Reward</h2>

            <div className="flex items-center justify-center gap-2 mb-4">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="text-lg font-semibold">{reward?.streak || 0} day streak</span>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-3xl font-bold text-blue-600">+{reward?.points || 10}</p>
              <p className="text-gray-500">points today</p>
            </div>

            <div className="text-sm text-gray-500 mb-6">
              <p>🔥 7 days = 20 points</p>
              <p>🔥 14 days = 30 points</p>
              <p>🔥 30 days = 50 points</p>
            </div>

            {isClaimed ? (
              <div className="flex items-center justify-center gap-2 text-green-600 font-medium">
                <Check className="w-5 h-5" />
                Already claimed today. Come back tomorrow!
              </div>
            ) : (
              <button
                onClick={handleClaim}
                disabled={claim.isPending}
                className="w-full py-3 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-xl font-semibold hover:from-orange-500 hover:to-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {claim.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Gift className="w-5 h-5" />
                )}
                Claim Reward
              </button>
            )}

            <button
              onClick={() => setShowModal(false)}
              className="mt-3 text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
